import { twMerge } from "tailwind-merge";
import { TableConfiguration, TableData } from "../../types";
import useTableColumnsConfig from "../../hooks/useSummaryTablesConfig";

export type TableProps = {
    title: string;
    data: TableData[];
    configuration?: TableConfiguration;
    id?: string;
};

export default function Table({
    title,
    data,
    configuration = TableConfiguration.DEFAULT,
    wide = false,
}: TableProps & {
    wide?: boolean;
}) {
    const fitWidthTables: TableConfiguration[] = [TableConfiguration.PERSON];
    const tableColumnsConfig = useTableColumnsConfig(configuration);

    const commonTrClasses = twMerge(
        "flex items-center justify-between text-center text-black px-1.5 2xl:px-3",
        wide ? "h-[26px] xl:h-[34px] gap-1.5 xl:gap-6" : "h-7 xl:h-10",
        wide &&
            configuration !== TableConfiguration.RESPONSES &&
            "justify-between",
        !wide && configuration === TableConfiguration.POST_SPIROMETRY
            ? "gap-1.5 xl:gap-3"
            : "gap-1 2xl:gap-1.5"
    );

    const trHeaderClasses = twMerge(
        "font-semibold shadow-[0px_2px_4px_0px_#00000026]",
        commonTrClasses,
        wide ? "text-xs xl:text-[15px]" : "text-xs xl:text-[15px] 2xl:text-base"
    );

    const trBodyClasses = twMerge(
        `h-[30px] xl:h-[38px] font-bold tracking-tight xl:tracking-normal`,
        commonTrClasses,
        wide ? "text-xs xl:text-[15px]" : "text-xs xl:text-[15px] 2xl:text-base"
    );

    return (
        <div
            className={twMerge(
                fitWidthTables.includes(configuration) && "w-fit"
            )}
        >
            <h6
                className={twMerge(
                    `text-black font-bold xl:text-lg`,
                    wide
                        ? "text-sm leading-6 xl:leading-7 mb-0.5"
                        : "text-base leading-8 xl:leading-normal xl:mt-4 mb-0.5 xl:mb-1"
                )}
            >
                {title}
            </h6>
            <table
                className={`flex flex-col rounded bg-white shadow-[0px_2px_8px_0px_#366CD640]`}
            >
                <thead className="">
                    <tr className={trHeaderClasses}>
                        {tableColumnsConfig.map((col) => (
                            <th
                                key={col.title}
                                className={twMerge(
                                    col.className,
                                    "font-semibold"
                                )}
                            >
                                {col.title}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody
                    className={`${
                        wide ? "flex flex-col justify-between h-full" : "block"
                    }  w-full`}
                >
                    {data.map((row) => (
                        <tr key={row.parameterName} className={trBodyClasses}>
                            {tableColumnsConfig.map((col) => {
                                const commonClass = "line-clamp-1 py-[3px]";
                                const value = col.valueGetter
                                    ? col.valueGetter(row)
                                    : (row as any)[col.fieldKey];
                                return (
                                    <td
                                        key={col.fieldKey}
                                        className={twMerge(
                                            commonClass,
                                            col.className
                                        )}
                                        dangerouslySetInnerHTML={
                                            col.asHTML
                                                ? { __html: value as string }
                                                : undefined
                                        }
                                    >
                                        {col.asHTML ? undefined : value}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
